/**
 * Client-side CSS for TestRelic Report.
 * Covers layout, theming, filter bar, test grid, drawer, network DevTools,
 * and all component styles.
 */
export const CSS = `
/* Theme Variables */
:root,[data-theme="dark"]{
  --bg:#0f1117;--bg-1:#161b22;--bg-2:#1c2128;--bg-3:#21262d;--bg-code:#13111c;
  --fg:#e6edf3;--fg-1:#8b949e;--fg-2:#484f58;--fg-code:#d4d4d4;--fg-err:#f8d7da;
  --bd:rgba(255,255,255,0.06);--bd-s:rgba(255,255,255,0.04);--bd-m:rgba(255,255,255,0.08);--bd-l:rgba(255,255,255,0.1);--bd-xs:rgba(255,255,255,0.03);
  --hvr:rgba(255,255,255,0.025);--hvr-s:rgba(255,255,255,0.02);
  --overlay-bg:rgba(0,0,0,.55);--shadow-c:rgba(0,0,0,.35);
  --lb-bg:rgba(0,0,0,.92);--lb-shadow:rgba(0,0,0,.6);--lb-btn:rgba(255,255,255,.1);--lb-btn-h:rgba(255,255,255,.2);
  --scroll-thumb:#21262d;
}
[data-theme="light"]{
  --bg:#ffffff;--bg-1:#f6f8fa;--bg-2:#eaeef2;--bg-3:#d0d7de;--bg-code:#f6f8fa;
  --fg:#1f2328;--fg-1:#656d76;--fg-2:#8b949e;--fg-code:#24292e;--fg-err:#82071e;
  --bd:rgba(0,0,0,0.08);--bd-s:rgba(0,0,0,0.04);--bd-m:rgba(0,0,0,0.12);--bd-l:rgba(0,0,0,0.15);--bd-xs:rgba(0,0,0,0.03);
  --hvr:rgba(0,0,0,0.04);--hvr-s:rgba(0,0,0,0.03);
  --overlay-bg:rgba(0,0,0,.3);--shadow-c:rgba(0,0,0,.1);
  --lb-bg:rgba(0,0,0,.85);--lb-shadow:rgba(0,0,0,.3);--lb-btn:rgba(0,0,0,.12);--lb-btn-h:rgba(0,0,0,.2);
  --scroll-thumb:#d0d7de;
}

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{
  font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  background:var(--bg);color:var(--fg);line-height:1.5;
  -webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;
}

/* ── Centered Container ── */
.wrap{max-width:960px;margin:0 auto;padding:32px 24px 64px}

/* ── Header ── */
.top-bar{display:flex;align-items:center;gap:14px;margin-bottom:20px;flex-wrap:wrap}
.brand{display:flex;align-items:center;gap:10px}
.brand svg{flex-shrink:0}
.brand-text{display:flex;flex-direction:column}
.brand-title{font-size:17px;font-weight:700;color:var(--fg);line-height:1.2}
.brand-sub{font-size:10px;font-weight:600;color:#03b79c;letter-spacing:.06em;text-transform:uppercase;margin-top:1px}
.run-meta{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:11px;color:var(--fg-1);margin-left:auto}
.run-meta-item{display:flex;align-items:center;gap:4px}
.run-meta-label{font-weight:600}
.run-id-tag{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;background:var(--bg-2);padding:1px 6px;border-radius:4px;color:var(--fg-1)}
.ci-tag{display:inline-flex;align-items:center;gap:4px;background:rgba(59,130,246,0.12);color:#60a5fa;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600}
.merged-tag{background:rgba(245,158,11,0.12);color:#fbbf24;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600}

/* ── Theme Toggle ── */
.theme-toggle{display:inline-flex;border:1px solid var(--bd-m);border-radius:8px;overflow:hidden;background:var(--bg);flex-shrink:0}
.theme-btn{padding:6px 10px;border:none;background:transparent;color:var(--fg-2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s;font-family:inherit}
.theme-btn:not(:last-child){border-right:1px solid var(--bd-m)}
.theme-btn.active{background:var(--bg-3);color:var(--fg)}
.theme-btn:hover:not(.active){color:var(--fg-1);background:var(--hvr)}
.theme-btn svg{width:14px;height:14px}

/* ── CTA Button ── */
.cta-btn{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:500;color:#e6edf3;background:linear-gradient(135deg,rgba(3,183,156,0.15),rgba(14,165,233,0.15));border:1px solid rgba(3,183,156,0.3);padding:6px 14px;border-radius:8px;text-decoration:none;white-space:nowrap;flex-shrink:0;transition:all .2s ease;letter-spacing:.01em}
.cta-btn:hover{background:linear-gradient(135deg,rgba(3,183,156,0.25),rgba(14,165,233,0.25));border-color:rgba(3,183,156,0.5);box-shadow:0 0 16px rgba(3,183,156,0.15);color:#fff}
.cta-btn strong{font-weight:700;color:#2dd4a8}
.cta-btn:hover strong{color:#5eead4}
.cta-icon{width:13px;height:13px;color:#2dd4a8;flex-shrink:0}
.cta-sep{color:var(--fg-2);margin:0 1px}
.cta-arrow{width:11px;height:11px;color:#2dd4a8;flex-shrink:0;transition:transform .2s}
.cta-btn:hover .cta-arrow{transform:translateX(2px)}
[data-theme="light"] .cta-btn{color:#1f2328;background:linear-gradient(135deg,rgba(3,183,156,0.08),rgba(14,165,233,0.08));border-color:rgba(3,183,156,0.25)}
[data-theme="light"] .cta-btn:hover{background:linear-gradient(135deg,rgba(3,183,156,0.15),rgba(14,165,233,0.15));border-color:rgba(3,183,156,0.4);color:#0f172a}
[data-theme="light"] .cta-btn strong{color:#059669}
[data-theme="light"] .cta-icon,[data-theme="light"] .cta-arrow{color:#059669}

/* ── Summary Strip ── */
.summary-strip{display:flex;gap:8px;margin-bottom:20px}
.summary-chip{flex:1;text-align:center;padding:14px 4px;border-radius:10px;cursor:default;border:1px solid var(--bd-s)}
.summary-chip .s-count{font-size:22px;font-weight:800;line-height:1}
.summary-chip .s-label{font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;margin-top:4px;color:var(--fg-2)}
.s-total{background:rgba(99,102,241,0.06)}.s-total .s-count{color:#818cf8}
.s-passed{background:rgba(34,197,94,0.06)}.s-passed .s-count{color:#22c55e}
.s-failed{background:rgba(239,68,68,0.06)}.s-failed .s-count{color:#ef4444}
.s-flaky{background:rgba(245,158,11,0.06)}.s-flaky .s-count{color:#f59e0b}
.s-skipped{background:rgba(107,114,128,0.06)}.s-skipped .s-count{color:#6b7280}
.s-timedout{background:rgba(249,115,22,0.06)}.s-timedout .s-count{color:#f97316}

/* ── Filter Bar ── */
.filter-bar{display:flex;align-items:center;gap:8px;margin-bottom:24px;flex-wrap:wrap}
.filter-chips{display:flex;gap:5px;flex-wrap:wrap}
.filter-chip{
  font-size:12px;font-weight:500;padding:5px 14px;border-radius:9999px;
  border:1px solid var(--bd-m);background:transparent;color:var(--fg-1);
  cursor:pointer;transition:all .15s;font-family:inherit;white-space:nowrap;
}
.filter-chip:hover{border-color:#03b79c;color:var(--fg)}
.filter-chip.active{background:rgba(3,183,156,0.12);border-color:#03b79c;color:#2dd4a8}
.chip-count{font-weight:700;margin-left:3px}
.filter-chip--zero{opacity:.35;pointer-events:none}
.filter-chip--dimmed{opacity:.45}
.filter-clear{font-size:11px;font-weight:600;color:#03b79c;background:none;border:none;cursor:pointer;padding:4px 8px;border-radius:6px;font-family:inherit;transition:background .15s}
.filter-clear:hover{background:rgba(3,183,156,0.1)}
.filter-indicator{font-size:11px;color:var(--fg-2)}

/* ── Filter Icon Button ── */
.filter-icon-btn{position:relative;display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:8px;border:1px solid var(--bd-m);background:transparent;color:var(--fg-2);cursor:pointer;transition:all .15s;flex-shrink:0;margin-left:auto}
.filter-icon-btn:hover{background:var(--hvr);color:var(--fg);border-color:var(--bd-l)}
.filter-icon-badge{position:absolute;top:5px;right:5px;width:7px;height:7px;border-radius:50%;background:#03b79c}

/* ── Filter Drawer ── */
.filter-drawer-backdrop{position:fixed;inset:0;background:var(--overlay-bg);z-index:100;opacity:0;pointer-events:none;transition:opacity .25s ease}
.filter-drawer-backdrop.open{opacity:1;pointer-events:auto}
.filter-drawer{position:fixed;top:0;right:0;bottom:0;width:340px;max-width:90vw;z-index:110;background:var(--bg-1);border-left:1px solid var(--bd);transform:translateX(100%);transition:transform .3s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;overflow:hidden;box-shadow:-4px 0 24px var(--shadow-c)}
.filter-drawer.open{transform:translateX(0)}
.filter-drawer-bar{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--bd);background:linear-gradient(180deg,rgba(3,183,156,0.04) 0%,transparent 100%);flex-shrink:0}
.filter-drawer-title{font-size:12px;font-weight:600;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em}
.filter-drawer-close{width:32px;height:32px;border-radius:8px;border:1px solid var(--bd-m);background:transparent;color:var(--fg-1);font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s,color .15s}
.filter-drawer-close:hover{background:var(--bd);color:var(--fg)}
.filter-drawer-body{flex:1;overflow-y:auto;padding:20px;scrollbar-width:thin;scrollbar-color:var(--scroll-thumb) transparent}
.filter-drawer-body::-webkit-scrollbar{width:5px}
.filter-drawer-body::-webkit-scrollbar-thumb{background:var(--bg-3);border-radius:3px}
.filter-drawer-section{margin-bottom:20px}
.filter-drawer-section-label{display:block;font-size:10px;font-weight:600;color:var(--fg-2);text-transform:uppercase;letter-spacing:.04em;margin-bottom:8px}
.filter-drawer-actions{display:flex;align-items:center;gap:12px;padding-top:16px;border-top:1px solid var(--bd)}
.search-box{position:relative;width:280px;flex-shrink:1;min-width:180px;margin-left:0}
.search-input{
  width:100%;padding:6px 10px 6px 32px;
  border:1px solid var(--bd-m);border-radius:8px;
  background:var(--bg-1);color:var(--fg);font-size:12px;font-family:inherit;outline:none;transition:border-color .15s;
}
.search-input::placeholder{color:var(--fg-2)}
.search-input:focus{border-color:#03b79c}
.search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--fg-2);pointer-events:none}

/* ── File Groups & Test Rows ── */
.file-group{margin-bottom:16px}
.file-group-header{
  font-size:11px;font-weight:600;
  font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
  color:var(--fg-2);padding:0 4px 6px;
}
.file-group-card{
  border:1px solid var(--bd);border-radius:10px;overflow:hidden;background:var(--bg-1);
}
.test-row{
  display:flex;align-items:center;gap:12px;padding:12px 18px;cursor:pointer;
  transition:background .1s;border-bottom:1px solid var(--bd-s);
}
.test-row:last-child{border-bottom:none}
.test-row:hover{background:var(--hvr)}
.test-row.active{background:rgba(3,183,156,0.07)}

.status-indicator{
  width:10px;height:10px;border-radius:50%;flex-shrink:0;
}
.si-passed{background:#22c55e}.si-failed{background:#ef4444}
.si-flaky{background:#f59e0b}.si-skipped{background:#6b7280}.si-timedout{background:#f97316}

.test-row-info{flex:1;min-width:0;display:flex;flex-direction:column;gap:3px}
.test-row-title{font-size:14px;font-weight:500;color:var(--fg);line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.test-row-badges{display:flex;gap:5px;align-items:center;flex-wrap:wrap}
.trb{font-size:10px;font-weight:600;padding:1px 7px;border-radius:4px}
.trb-type{background:rgba(139,92,246,0.12);color:#a78bfa}
.trb-tag{background:rgba(59,130,246,0.12);color:#60a5fa}
.trb-retry{background:rgba(245,158,11,0.12);color:#fbbf24}
.trb-flaky{background:rgba(245,158,11,0.12);color:#fbbf24}
.test-row-dur{font-size:13px;font-weight:700;color:var(--fg-1);flex-shrink:0;white-space:nowrap}
.test-row-arrow{color:var(--fg-2);flex-shrink:0;transition:color .15s}
.test-row:hover .test-row-arrow{color:var(--fg-1)}

.no-tests{text-align:center;padding:48px 20px;color:var(--fg-2);font-size:14px}

/* ── Drawer Overlay ── */
.drawer-backdrop{
  position:fixed;inset:0;background:var(--overlay-bg);z-index:100;
  opacity:0;pointer-events:none;transition:opacity .25s ease;
}
.drawer-backdrop.open{opacity:1;pointer-events:auto}

/* ── Drawer Panel ── */
.drawer{
  position:fixed;top:0;right:0;bottom:0;width:50vw;max-width:50vw;z-index:110;
  background:var(--bg-1);border-left:1px solid var(--bd);
  transform:translateX(100%);transition:transform .3s cubic-bezier(.4,0,.2,1);
  display:flex;flex-direction:column;overflow:hidden;
  box-shadow:-8px 0 40px var(--shadow-c);
}
.drawer.open{transform:translateX(0)}
.drawer-bar{
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 20px;border-bottom:1px solid var(--bd);
  background:linear-gradient(180deg,rgba(3,183,156,0.04) 0%,transparent 100%);flex-shrink:0;
}
.drawer-bar-title{font-size:12px;font-weight:600;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em}
.drawer-close{
  width:32px;height:32px;border-radius:8px;border:1px solid var(--bd-m);
  background:transparent;color:var(--fg-1);font-size:18px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:background .15s,color .15s;
}
.drawer-close:hover{background:var(--bd);color:var(--fg)}
.drawer-body{flex:1;overflow-y:auto;padding:24px 28px;scrollbar-width:thin;scrollbar-color:var(--scroll-thumb) transparent}
.drawer-body::-webkit-scrollbar{width:5px}
.drawer-body::-webkit-scrollbar-track{background:transparent}
.drawer-body::-webkit-scrollbar-thumb{background:var(--bg-3);border-radius:3px}

/* ── Drawer Sections ── */
.drawer-sections{display:flex;flex-direction:column;gap:24px}
.drawer-section{min-width:0}

/* ── Segmented Control ── */
.seg-ctrl{display:inline-flex;border:1px solid var(--bd-l);border-radius:8px;overflow:hidden;background:var(--bg);margin-bottom:12px}
.seg-btn{padding:7px 16px;font-size:11px;font-weight:600;color:var(--fg-2);background:transparent;border:none;cursor:pointer;font-family:inherit;transition:all .15s;white-space:nowrap;display:flex;align-items:center;gap:5px}
.seg-btn:not(:last-child){border-right:1px solid var(--bd-l)}
.seg-btn.active{background:rgba(3,183,156,0.12);color:#2dd4a8}
.seg-btn:hover:not(.active){color:var(--fg-1);background:var(--bd-xs)}
.seg-pane{display:none}
.seg-pane.active{display:block;animation:trFadeIn .15s ease-out}

/* ── Artifact Column ── */
.artifact-col-img{max-width:100%;border-radius:8px;cursor:pointer;border:1px solid var(--bd);transition:border-color .15s,box-shadow .15s;object-fit:contain;display:block}
.artifact-col-img:hover{border-color:#03b79c;box-shadow:0 0 0 3px rgba(3,183,156,0.15)}
.artifact-col-caption{font-size:10px;color:var(--fg-2);margin-top:6px;display:flex;align-items:center;gap:4px}
.artifact-col-caption svg{opacity:.5}
.artifact-col-video{border-radius:8px;overflow:hidden;border:1px solid var(--bd);background:#000}
.artifact-col-video video{width:100%;display:block}
.artifact-empty{padding:24px 14px;text-align:center;font-size:12px;color:var(--fg-2);font-style:italic;border:1px dashed var(--bd-m);border-radius:10px}

/* ── Network Overview ── */
.net-overview{border:1px solid var(--bd);border-radius:10px;overflow:hidden;background:var(--bg-2)}
.net-section-hd{padding:8px 12px;font-size:10px;font-weight:600;color:var(--fg-2);text-transform:uppercase;letter-spacing:.06em;border-top:1px solid var(--bd-s);display:flex;align-items:center;gap:6px}
.net-section-hd svg{opacity:.5}

/* ── Resource Bar Chart ── */
.net-bar-wrap{padding:10px 12px 4px}
.net-bar{display:flex;height:20px;border-radius:5px;overflow:hidden;background:var(--bg-3)}
.net-bar-seg{min-width:2px;position:relative;transition:opacity .15s}
.net-bar-seg:hover{opacity:.8}
.net-bar--mini{height:6px;border-radius:3px;margin-bottom:4px}
.net-bar--mini .net-bar-seg{min-width:1px}
.net-bar-legend{display:flex;flex-wrap:wrap;gap:4px 12px;padding:4px 12px 8px;font-size:10px}
.net-legend-item{display:flex;align-items:center;gap:4px;color:var(--fg-1)}
.net-legend-dot{width:8px;height:8px;border-radius:2px;flex-shrink:0}
.net-legend-count{font-weight:700;color:var(--fg)}

/* ── Network Step Cards ── */
.net-steps-list{border-top:1px solid var(--bd-s)}
.net-step-card{padding:10px 12px;border-bottom:1px solid var(--bd-xs)}
.net-step-card:last-child{border-bottom:none}
.net-step-card-head{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.net-step-num{width:20px;height:20px;border-radius:50%;background:var(--bg-3);color:var(--fg-1);font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.net-step-url{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--fg-1);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px}
.net-step-stats{flex-shrink:0;color:var(--fg-2);font-size:10px;white-space:nowrap}
.net-step-timing{display:flex;gap:10px;font-size:9px;color:var(--fg-2);flex-wrap:wrap}
.net-step-timing span{display:flex;align-items:center;gap:3px}
.timing-label{font-weight:700;text-transform:uppercase;letter-spacing:.04em;font-size:8px}
.timing-val{font-weight:600;color:var(--fg-1)}

/* ── Failed URL badges ── */
.fail-url-badge{display:inline-flex;align-items:center;font-size:10px;font-weight:700;padding:1px 5px;border-radius:3px;margin-right:5px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.fail-url-badge--4xx{background:rgba(245,158,11,0.12);color:#f59e0b}
.fail-url-badge--5xx{background:rgba(239,68,68,0.12);color:#ef4444}
.fail-url-badge--other{background:rgba(107,114,128,0.1);color:#6b7280}

/* ── Drawer Content — Test Detail ── */
.test-detail{animation:trFadeIn .2s ease-out}
@keyframes trFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}

.test-detail-header{margin-bottom:22px;padding-bottom:18px;border-bottom:1px solid var(--bd)}
.test-detail-top{display:flex;align-items:flex-start;gap:12px;margin-bottom:10px}
.detail-status-badge{
  font-size:11px;font-weight:700;padding:4px 14px;border-radius:9999px;
  text-transform:uppercase;letter-spacing:.04em;flex-shrink:0;margin-top:2px;
}
.dbg-passed{background:rgba(34,197,94,0.1);color:#22c55e;border:1px solid rgba(34,197,94,0.2)}
.dbg-failed{background:rgba(239,68,68,0.1);color:#ef4444;border:1px solid rgba(239,68,68,0.2)}
.dbg-flaky{background:rgba(245,158,11,0.1);color:#f59e0b;border:1px solid rgba(245,158,11,0.2)}
.dbg-skipped{background:rgba(107,114,128,0.1);color:#6b7280;border:1px solid rgba(107,114,128,0.2)}
.dbg-timedout{background:rgba(249,115,22,0.1);color:#f97316;border:1px solid rgba(249,115,22,0.2)}

.detail-title-section{flex:1;min-width:0}
.detail-title{font-size:16px;font-weight:700;color:var(--fg);line-height:1.35;margin-bottom:6px}
.detail-meta{display:flex;flex-wrap:wrap;gap:5px;align-items:center}
.dm-badge{font-size:10px;font-weight:600;padding:2px 8px;border-radius:4px}
.dm-type{background:rgba(139,92,246,0.12);color:#a78bfa}
.dm-tag{background:rgba(59,130,246,0.12);color:#60a5fa}
.dm-retry{background:rgba(245,158,11,0.12);color:#fbbf24}
.dm-flaky{background:rgba(245,158,11,0.12);color:#fbbf24}
.detail-sub-meta{display:flex;gap:14px;font-size:11px;color:var(--fg-1);flex-wrap:wrap;margin-top:6px}
.meta-k{font-weight:600;color:var(--fg-2);text-transform:uppercase;letter-spacing:.04em;font-size:10px;margin-right:3px}
.detail-id{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;color:var(--fg-1);background:var(--bg-2);padding:1px 6px;border-radius:4px}
.detail-file{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;color:var(--fg-1)}
.detail-suite{font-size:11px;color:var(--fg-1)}
.detail-duration{font-size:24px;font-weight:800;color:var(--fg);flex-shrink:0;white-space:nowrap}

/* ── Failure Panel ── */
.failure-panel{margin-bottom:20px;border:1px solid rgba(239,68,68,0.2);border-radius:10px;overflow:hidden;background:var(--bg-2)}
.failure-panel-header{display:flex;align-items:center;gap:8px;padding:10px 14px;background:rgba(239,68,68,0.08);font-size:12px;font-weight:600;color:#ef4444}
.copy-prompt-btn{margin-left:auto;display:inline-flex;align-items:center;gap:5px;padding:4px 10px;font-size:11px;font-weight:500;font-family:inherit;color:var(--fg-1);background:var(--bg-3);border:1px solid var(--bd);border-radius:6px;cursor:pointer;transition:all .15s;white-space:nowrap}
.copy-prompt-btn:hover{background:var(--bg-2);color:var(--fg);border-color:var(--bd-m)}
.copy-prompt-btn.copied{color:#34d399;border-color:rgba(52,211,153,0.3);background:rgba(52,211,153,0.08)}
.failure-message{padding:12px 14px;background:var(--bg-code);color:var(--fg-err);font-size:12px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-word;line-height:1.6;overflow-x:auto}
.code-snippet{background:var(--bg-code);font-size:12px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;overflow-x:auto;border-top:1px solid var(--bd-s)}
.code-line{display:flex;padding:0 14px;line-height:1.8}
.code-line.highlight{background:rgba(239,68,68,0.12)}
.line-num{color:var(--fg-2);min-width:36px;text-align:right;padding-right:14px;user-select:none;flex-shrink:0}
.line-code{color:var(--fg-code);white-space:pre;overflow-x:auto}
.line-marker{padding:4px 14px;font-size:10px;color:var(--fg-2);background:var(--bg-2);border-top:1px solid var(--bd-s)}
.stack-toggle-btn{font-size:11px;color:var(--fg-1);cursor:pointer;padding:8px 14px;border:none;background:var(--bg-3);width:100%;text-align:left;font-family:inherit;border-top:1px solid var(--bd-s);transition:background .15s}
.stack-toggle-btn:hover{background:var(--bg-2);color:var(--fg)}
.stack-trace{display:none;padding:12px 14px;background:var(--bg-2);font-size:11px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-word;color:var(--fg-2);border-top:1px solid var(--bd-s);max-height:260px;overflow-y:auto}
.stack-trace.show{display:block}

/* ── Section Label ── */
.section-label{font-size:11px;font-weight:700;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em;margin-bottom:14px;display:flex;align-items:center;gap:8px}
.section-label::before{content:'';width:3px;height:14px;background:#03b79c;border-radius:2px}

/* ── Artifacts Panel ── */
.artifacts-panel{margin-bottom:20px;border:1px solid var(--bd);border-radius:10px;overflow:hidden;background:var(--bg-2)}
.artifacts-toolbar{display:flex;align-items:center;gap:0;padding:0;background:var(--bg-3);border-bottom:1px solid var(--bd-s)}
.artifacts-label{display:flex;align-items:center;gap:6px;padding:9px 14px;font-size:11px;font-weight:600;color:var(--fg-1);text-transform:uppercase;letter-spacing:.04em;border-right:1px solid var(--bd-s);flex-shrink:0}
.artifact-tab{padding:9px 16px;font-size:12px;font-weight:500;color:var(--fg-2);cursor:pointer;border:none;background:transparent;font-family:inherit;transition:color .15s,background .15s;position:relative;display:flex;align-items:center;gap:6px}
.artifact-tab:hover{color:var(--fg-1);background:var(--hvr-s)}
.artifact-tab.active{color:var(--fg);background:var(--bd-xs)}
.artifact-tab.active::after{content:'';position:absolute;bottom:0;left:8px;right:8px;height:2px;background:#03b79c;border-radius:1px}
.artifact-tab svg{flex-shrink:0;opacity:.6}
.artifact-tab.active svg{opacity:1}
.artifact-pane{display:none;padding:14px}
.artifact-pane.active{display:block;animation:trFadeIn .15s ease-out}
.screenshot-pane{display:flex;flex-direction:column;align-items:center;gap:8px}
.artifact-thumb{max-height:260px;max-width:100%;width:auto;border-radius:8px;cursor:pointer;border:1px solid var(--bd);transition:border-color .15s,box-shadow .15s,transform .15s;object-fit:contain;display:block}
.artifact-thumb:hover{border-color:#03b79c;box-shadow:0 0 0 3px rgba(3,183,156,0.15);transform:scale(1.01)}
.artifact-caption{font-size:11px;color:var(--fg-2);display:flex;align-items:center;gap:4px}
.artifact-caption svg{opacity:.5}
.video-pane{display:flex;flex-direction:column;gap:0}
.video-player{border-radius:8px;overflow:hidden;border:1px solid var(--bd);background:#000}
.video-player video{width:100%;max-height:340px;display:block}

/* ── Navigation Timeline ── */
.nav-timeline{position:relative;margin-bottom:20px}
.nav-step{display:flex;gap:12px;position:relative}
.step-connector{display:flex;flex-direction:column;align-items:center;flex-shrink:0;width:28px}
.step-node{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0;z-index:1;border:2px solid;transition:transform .15s}
.step-node:hover{transform:scale(1.1)}
.node-ok{background:rgba(3,183,156,0.1);border-color:rgba(3,183,156,0.35);color:#2dd4a8}
.node-warn{background:rgba(245,158,11,0.1);border-color:rgba(245,158,11,0.35);color:#f59e0b}
.step-line{width:2px;flex:1;min-height:10px;background:linear-gradient(to bottom,rgba(3,183,156,0.35),rgba(3,183,156,0.08))}
.nav-step:last-child .step-line{display:none}
.step-content{flex:1;min-width:0;padding-bottom:14px}
.nav-step:last-child .step-content{padding-bottom:0}
.step-header{display:flex;align-items:center;gap:7px;cursor:pointer;padding:4px 8px;border-radius:6px;margin:-4px -8px;transition:background .1s;flex-wrap:wrap}
.step-header:hover{background:var(--hvr-s)}
.step-url{font-size:12px;font-weight:500;color:var(--fg);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;min-width:0}
.nav-badge{font-size:9px;font-weight:600;padding:2px 7px;border-radius:9999px;text-transform:uppercase;letter-spacing:.03em;flex-shrink:0;white-space:nowrap}
.nb-goto{background:rgba(59,130,246,0.12);color:#60a5fa}
.nb-navigation{background:rgba(99,102,241,0.12);color:#818cf8}
.nb-page_load{background:rgba(129,140,248,0.12);color:#a5b4fc}
.nb-back{background:rgba(34,211,238,0.12);color:#22d3ee}
.nb-forward{background:rgba(34,211,238,0.12);color:#67e8f9}
.nb-spa_route{background:rgba(139,92,246,0.12);color:#a78bfa}
.nb-spa_replace{background:rgba(167,139,250,0.12);color:#c4b5fd}
.nb-hash_change{background:rgba(236,72,153,0.12);color:#f472b6}
.nb-popstate{background:rgba(244,114,182,0.12);color:#f9a8d4}
.nb-link_click{background:rgba(251,113,133,0.12);color:#fb7185}
.nb-form_submit{background:rgba(244,63,94,0.12);color:#fb7185}
.nb-redirect{background:rgba(249,115,22,0.12);color:#fb923c}
.nb-refresh{background:rgba(56,189,248,0.12);color:#38bdf8}
.nb-dummy{background:rgba(107,114,128,0.06);color:var(--fg-2)}
.nb-fallback{background:rgba(107,114,128,0.06);color:var(--fg-2)}
.nb-manual_record{background:rgba(234,179,8,0.12);color:#facc15}
.step-meta{display:flex;align-items:center;gap:7px;font-size:11px;color:var(--fg-2);margin-left:auto;flex-shrink:0}

/* Step expandable detail */
.step-detail{display:none;margin-top:8px;border:1px solid var(--bd);border-radius:8px;overflow:hidden;background:var(--bg-2)}
.step-detail.show{display:block;animation:trSlideDown .2s ease-out}
@keyframes trSlideDown{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
.net-header{display:flex;align-items:center;gap:6px;padding:7px 12px;font-size:11px;font-weight:600;color:var(--fg-1);background:var(--bg-3);border-bottom:1px solid var(--bd-s)}
.net-header.has-fails{color:#ef4444;background:rgba(239,68,68,0.08)}
.net-stats-row{display:flex;gap:8px;padding:8px 12px;flex-wrap:wrap}
.net-stat{display:flex;flex-direction:column;align-items:center;gap:1px;padding:5px 10px;border-radius:6px;background:var(--bg-3);min-width:50px}
.net-stat .nv{font-weight:700;font-size:14px;color:var(--fg);line-height:1}
.net-stat .nl{color:var(--fg-2);font-size:8px;font-weight:600;text-transform:uppercase;letter-spacing:.06em}
.net-stat.net-fail{background:rgba(239,68,68,0.08)}.net-stat.net-fail .nv{color:#ef4444}
.resource-row{display:flex;gap:4px;padding:7px 12px;flex-wrap:wrap;border-top:1px solid var(--bd-xs)}
.res-type{display:flex;align-items:center;gap:3px;font-size:10px;padding:2px 7px;border-radius:4px;background:var(--bg-3);color:var(--fg-1);font-weight:500}
.res-type .rc{font-weight:700}
.res-type--zero{opacity:.25}
.fail-urls{padding:7px 12px;border-top:1px solid rgba(239,68,68,0.12);background:rgba(239,68,68,0.06);max-height:160px;overflow-y:auto}
.fail-url{font-size:10px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:#ef4444;padding:2px 0;word-break:break-all}
.fail-url-status{font-weight:700;margin-right:4px}

/* ── Network DevTools Panel ── */
.ndt{border:1px solid var(--bd);border-radius:10px;overflow:hidden;background:var(--bg-1)}
.ndt{border:1px solid var(--bd);border-radius:8px;overflow:hidden;background:var(--bg-1)}
.ndt-toolbar{display:flex;align-items:center;gap:4px;padding:4px 8px;background:var(--bg-2);border-bottom:1px solid var(--bd);flex-wrap:wrap}
.ndt-filter{display:flex;gap:2px;flex-wrap:wrap}
.ndt-filter-btn{font-size:10px;padding:2px 8px;border-radius:4px;border:1px solid transparent;background:none;color:var(--fg-2);cursor:pointer;font-weight:500;transition:all .15s}
.ndt-filter-btn:hover{background:var(--hvr);color:var(--fg-1)}
.ndt-filter-btn.active{background:var(--bg-3);color:var(--fg);border-color:var(--bd-m);font-weight:600}
.ndt-search{flex:1;min-width:100px;max-width:220px;font-size:10px;padding:3px 8px;border-radius:4px;border:1px solid var(--bd);background:var(--bg);color:var(--fg);outline:none;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.ndt-search:focus{border-color:rgba(3,183,156,0.4)}
.ndt-count{font-size:10px;color:var(--fg-2);margin-left:auto;white-space:nowrap}
.ndt-list{max-height:600px;overflow-y:auto;background:var(--bg)}
.ndt-list::-webkit-scrollbar{width:5px}.ndt-list::-webkit-scrollbar-thumb{background:var(--scroll-thumb);border-radius:4px}
.ndt-row{display:grid;grid-template-columns:42px 50px 1fr 52px 56px 56px;align-items:center;gap:4px;padding:4px 10px;border-bottom:1px solid var(--bd-xs);cursor:pointer;font-size:11px;transition:background .1s}
.ndt-row:hover{background:var(--hvr)}
.ndt-row.ndt-row--open{background:var(--bg-2)}
.ndt-row.ndt-row--fail{background:rgba(239,68,68,0.04)}
.ndt-row.ndt-row--fail:hover{background:rgba(239,68,68,0.08)}
.ndt-row.seekable{cursor:pointer}
.ndt-row--active{background:rgba(3,183,156,0.1) !important;border-left:3px solid #03b79c}
.ndt-row--active .ndt-url{color:#2dd4a8}
.ndt-offset{color:#03b79c !important;font-weight:600}
.ndt-status{font-weight:700;font-size:10px;padding:1px 5px;border-radius:3px;text-align:center;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.ndt-status--2xx{background:rgba(34,197,94,0.12);color:#22c55e}
.ndt-status--3xx{background:rgba(59,130,246,0.12);color:#60a5fa}
.ndt-status--4xx{background:rgba(249,115,22,0.12);color:#fb923c}
.ndt-status--5xx{background:rgba(239,68,68,0.12);color:#ef4444}
.ndt-status--0{background:rgba(239,68,68,0.12);color:#ef4444}
.ndt-method{font-weight:600;font-size:10px;color:var(--fg-1);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;text-transform:uppercase}
.ndt-url{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--fg);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px}
.ndt-type{font-size:9px;padding:1px 5px;border-radius:3px;background:var(--bg-3);color:var(--fg-2);text-align:center;font-weight:500}
.ndt-size{font-size:10px;color:var(--fg-2);text-align:right;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.ndt-time{font-size:10px;color:var(--fg-2);text-align:right;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.ndt-detail{display:none;border-bottom:1px solid var(--bd);background:var(--bg);animation:trSlideDown .15s ease-out}
.ndt-detail.show{display:block}
.ndt-detail-tabs{display:flex;gap:0;border-bottom:1px solid var(--bd);background:var(--bg-2)}
.ndt-detail-tab{font-size:10px;padding:6px 14px;border:none;background:none;color:var(--fg-2);cursor:pointer;font-weight:500;border-bottom:2px solid transparent;transition:all .15s}
.ndt-detail-tab:hover{color:var(--fg-1);background:var(--hvr)}
.ndt-detail-tab.active{color:#03b79c;border-bottom-color:#03b79c;font-weight:600}
.ndt-detail-pane{display:none;padding:10px 14px;max-height:360px;overflow-y:auto}
.ndt-detail-pane::-webkit-scrollbar{width:5px}.ndt-detail-pane::-webkit-scrollbar-thumb{background:var(--scroll-thumb);border-radius:4px}
.ndt-detail-pane.active{display:block}
.ndt-general{display:grid;grid-template-columns:auto 1fr;gap:4px 14px;font-size:11px}
.ndt-general-k{color:var(--fg-2);font-weight:600;white-space:nowrap}
.ndt-general-v{color:var(--fg);word-break:break-all;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px}
.ndt-general-v.ndt-err{color:#ef4444}
.ndt-headers-tbl{width:100%;border-collapse:collapse;font-size:10px}
.ndt-headers-tbl th{text-align:left;padding:4px 8px;background:var(--bg-2);color:var(--fg-2);font-weight:600;border-bottom:1px solid var(--bd);font-size:9px;text-transform:uppercase;letter-spacing:.04em}
.ndt-headers-tbl td{padding:4px 8px;border-bottom:1px solid var(--bd-xs);vertical-align:top}
.ndt-headers-tbl td:first-child{color:var(--fg-1);font-weight:600;white-space:nowrap;width:180px}
.ndt-headers-tbl td:last-child{color:var(--fg);word-break:break-all;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.ndt-body-wrap{position:relative}
.ndt-body-pre{margin:0;padding:10px;background:var(--bg-code);border-radius:6px;border:1px solid var(--bd);font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;color:var(--fg-code);white-space:pre-wrap;word-break:break-all;max-height:320px;overflow-y:auto;line-height:1.5;tab-size:2}
.ndt-body-pre::-webkit-scrollbar{width:5px}.ndt-body-pre::-webkit-scrollbar-thumb{background:var(--scroll-thumb);border-radius:4px}
.ndt-body-empty{padding:16px;text-align:center;font-size:11px;color:var(--fg-2);font-style:italic}
.ndt-body-truncated{display:inline-block;margin-top:6px;font-size:9px;padding:2px 8px;border-radius:4px;background:rgba(249,115,22,0.1);color:#fb923c;font-weight:500}
.ndt-hdr-section{margin-bottom:10px}
.ndt-hdr-label{font-size:10px;font-weight:600;color:var(--fg-1);margin-bottom:4px;display:flex;align-items:center;gap:5px}
.ndt-hdr-label svg{opacity:.5}

/* ── Action Steps ── */
.actions-header{font-size:11px;font-weight:700;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px;display:flex;align-items:center;gap:6px}
.actions-header svg{opacity:.6}
.actions-count{font-weight:400;color:var(--fg-2);font-size:10px}
.actions-timeline{display:flex;flex-direction:column;gap:0}
.action-connector{width:2px;height:6px;background:var(--bd-m);margin-left:18px}
.action-step{display:flex;align-items:center;gap:8px;padding:6px 12px;border-radius:6px;font-size:12px;transition:background .1s}
.action-step:hover{background:var(--hvr)}
.action-badge{font-size:9px;font-weight:600;padding:2px 7px;border-radius:4px;text-transform:uppercase;letter-spacing:.03em;flex-shrink:0;white-space:nowrap}
.action-badge-ui{background:rgba(59,130,246,0.12);color:#60a5fa}
.action-badge-assertion{background:rgba(34,197,94,0.12);color:#22c55e}
.action-badge-assertion.failed{background:rgba(239,68,68,0.12);color:#ef4444}
.action-badge-custom{background:rgba(107,114,128,0.1);color:var(--fg-2)}
.action-title{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--fg)}
.action-duration{font-size:10px;color:var(--fg-2);flex-shrink:0;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.action-failed{background:rgba(239,68,68,0.04)}
.action-failed:hover{background:rgba(239,68,68,0.08)}
.action-error{font-size:11px;color:#ef4444;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-word;line-height:1.5;padding:4px 12px 8px;margin-bottom:2px}
.action-step.seekable{cursor:pointer}
.action-step.seekable:hover{background:rgba(3,183,156,0.06)}
.action-offset{font-size:9px;color:#03b79c;font-weight:600;flex-shrink:0;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;padding:1px 5px;border-radius:3px;background:rgba(3,183,156,0.08)}
.action-active{background:rgba(3,183,156,0.12);border-left:3px solid #03b79c;transition:background .2s,border-left .2s}
.action-active .action-title{color:#2dd4a8}
.action-active .action-offset{background:rgba(3,183,156,0.2)}
.action-step.seeking{animation:trSeekFlash .4s ease-out}
@keyframes trSeekFlash{0%{background:rgba(3,183,156,0.15)}100%{background:transparent}}

/* ── Console/Logs Panel ── */
.console-panel{border:1px solid var(--bd);border-radius:8px;overflow:hidden;background:var(--bg-1)}
.console-header{font-size:11px;font-weight:700;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em;display:flex;align-items:center;gap:6px;padding:8px 12px;border-bottom:1px solid var(--bd);background:var(--bg-2)}
.console-header svg{opacity:.6}
.console-count{font-weight:400;color:var(--fg-2);font-size:10px}
.console-error-count{color:#f87171;font-weight:500;font-size:10px;text-transform:none;letter-spacing:0;background:rgba(248,113,113,0.1);padding:1px 6px;border-radius:3px}
.console-warn-count{color:#fbbf24;font-weight:500;font-size:10px;text-transform:none;letter-spacing:0;background:rgba(251,191,36,0.1);padding:1px 6px;border-radius:3px}
.console-toolbar{display:flex;gap:3px;padding:4px 8px;border-bottom:1px solid var(--bd);flex-wrap:wrap;background:var(--bg-2)}
.console-filter-btn{font-size:10px;padding:2px 8px;border-radius:4px;border:1px solid transparent;background:transparent;color:var(--fg-2);cursor:pointer;transition:all .15s;font-family:inherit}
.console-filter-btn.active{background:var(--bg-3);color:var(--fg-1);border-color:var(--bd-m);font-weight:600}
.console-filter-btn:hover{background:var(--hvr);color:var(--fg-1)}
.console-list{max-height:400px;overflow-y:auto;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;background:var(--bg)}
.console-list::-webkit-scrollbar{width:5px}.console-list::-webkit-scrollbar-thumb{background:var(--scroll-thumb);border-radius:4px}
.console-entry{display:flex;align-items:baseline;gap:6px;padding:2px 10px;border-bottom:1px solid var(--bd-xs);line-height:20px}
.console-entry:hover{background:var(--hvr)}
.console-level-badge{font-weight:600;font-size:9px;min-width:32px;text-transform:uppercase;letter-spacing:.03em}
.console-time{color:var(--fg-2);font-size:10px;white-space:nowrap;min-width:65px}
.console-text{flex:1;word-break:break-word;white-space:pre-wrap;color:var(--fg)}
.console-loc{color:var(--fg-2);font-size:10px;white-space:nowrap;margin-left:auto;max-width:180px;overflow:hidden;text-overflow:ellipsis}
.console-entry.seekable{cursor:pointer}
.console-entry.seekable:hover{background:rgba(3,183,156,0.06)}
.console-active{background:rgba(3,183,156,0.12) !important;border-left:3px solid #03b79c}
.console-active .console-text{color:#2dd4a8}
.console-offset{color:#03b79c;font-size:9px;font-weight:600;min-width:42px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.console-level-error,.console-level-stderr{background:rgba(248,113,113,0.06)}
.console-level-warn{background:rgba(251,191,36,0.06)}

/* ── Lightbox ── */
.lightbox-overlay{position:fixed;inset:0;background:var(--lb-bg);z-index:1000;display:flex;align-items:center;justify-content:center;cursor:zoom-out;animation:trFadeIn .15s}
.lightbox-overlay img{max-width:92vw;max-height:92vh;border-radius:8px;box-shadow:0 8px 40px var(--lb-shadow)}
.lightbox-close{position:fixed;top:16px;right:16px;z-index:1001;width:40px;height:40px;border-radius:50%;border:none;background:var(--lb-btn);color:#fff;font-size:22px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;backdrop-filter:blur(8px)}
.lightbox-close:hover{background:var(--lb-btn-h)}

/* ── Responsive ── */
@media(max-width:1100px){
  .drawer{width:60vw;max-width:60vw}
}
@media(max-width:800px){
  .drawer{width:85vw;max-width:85vw}
}
@media(max-width:640px){
  .wrap{padding:16px 12px 48px}
  .top-bar{flex-direction:column;align-items:flex-start;gap:8px}
  .run-meta{margin-left:0}
  .summary-strip{flex-wrap:wrap}
  .summary-chip{min-width:70px}
  .filter-bar{flex-direction:column;align-items:stretch}
  .search-box{width:100%;margin-left:0}
  .filter-icon-btn{margin-left:auto}
  .filter-drawer{width:100%;max-width:100%}
  .drawer{width:100%;max-width:100%}
  .cta-btn{font-size:10px;padding:5px 10px;order:10}
  .detail-title{font-size:14px}
  .detail-duration{font-size:20px}
}

/* ── Print ── */
@media print{
  body{background:#fff;color:#000}
  .drawer-backdrop,.drawer,.filter-drawer-backdrop,.filter-drawer{display:none}
  .lightbox-overlay,.lightbox-close{display:none}
  .test-row-arrow{display:none}
  .summary-chip,.filter-chip,.status-indicator,.detail-status-badge,.dm-badge,.nav-badge,.trb,.step-node{
    print-color-adjust:exact;-webkit-print-color-adjust:exact;
  }
}
/* Loading Overlay */
.loading-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:var(--bg);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px}
.loading-spinner{width:36px;height:36px;border:3px solid var(--bd-l);border-top-color:var(--fg-1);border-radius:50%;animation:spin .8s linear infinite}
.loading-text{font:13px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:var(--fg-1)}
@keyframes spin{to{transform:rotate(360deg)}}
/* Global scrollbar styling */
*{scrollbar-width:thin;scrollbar-color:var(--scroll-thumb) transparent}
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:var(--scroll-thumb);border-radius:3px}
::-webkit-scrollbar-thumb:hover{background:var(--fg-2)}
::-webkit-scrollbar-corner{background:transparent}
/* Virtual grid sentinel */
.vg-sentinel{height:1px}
/* ── Streaming: detail loading / error states ── */
.detail-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 20px;gap:14px}
.detail-loading .loading-spinner{width:28px;height:28px;border:3px solid var(--bd-l);border-top-color:var(--fg-1);border-radius:50%;animation:spin .8s linear infinite}
.detail-loading .loading-text{font-size:12px;color:var(--fg-2)}
.detail-error{padding:32px 20px;text-align:center;color:var(--fg-2);font-size:13px;line-height:1.7}
.detail-error .failure-message{margin-bottom:16px;padding:12px;background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.15);border-radius:8px;font-size:12px;color:#f87171;text-align:left;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-word}
.detail-error code{font-size:11px;padding:2px 6px;background:var(--bg-2);border-radius:4px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.server-offline-banner{padding:8px 14px;background:rgba(251,191,36,0.08);border:1px solid rgba(251,191,36,0.2);border-radius:6px;font-size:11px;color:#fbbf24;text-align:center;margin-bottom:12px}
`;
